import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wrench, Search, Phone, Mail, Settings, Tag, AlertCircle, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tecnici = () => {
  const { getAuthHeaders, isAdmin, locali } = useAuth();
  const [items, setItems] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipologia, setFiltroTipologia] = useState("all");
  const [form, setForm] = useState({ 
    nome: "", 
    tipologia_id: "",
    telefono: "", 
    email: "",
    note: "",
    locali_ids: []
  });
  const [deleteId, setDeleteId] = useState(null);
  
  // Gestione tipologie
  const [tipologiaDialogOpen, setTipologiaDialogOpen] = useState(false);
  const [tipologiaForm, setTipologiaForm] = useState({ nome: "", icona: "🛠️", keywords: "" });
  const [editTipologia, setEditTipologia] = useState(null);
  const [deleteTipologiaId, setDeleteTipologiaId] = useState(null);

  const fetchData = async () => {
    try {
      const [tecniciRes, tipologieRes] = await Promise.all([
        axios.get(`${API}/tecnici`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tipologie-tecnico`, { headers: getAuthHeaders() })
      ]);
      setItems(tecniciRes.data);
      setTipologie(tipologieRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleLocale = (localeId) => {
    if (locali && locali.length === 1) return; // Locale unico: non deselezionabile
    setForm(prev => ({
      ...prev,
      locali_ids: prev.locali_ids.includes(localeId)
        ? prev.locali_ids.filter(id => id !== localeId)
        : [...prev.locali_ids, localeId]
    }));
  };

  // Ricerca con keywords
  const searchTecnici = async (query) => {
    if (!query) {
      fetchData();
      return;
    }
    try {
      const res = await axios.get(`${API}/tecnici?search=${encodeURIComponent(query)}`, { headers: getAuthHeaders() });
      setItems(res.data);
    } catch (error) {
      console.error("Errore ricerca");
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchTecnici(searchTerm);
      } else {
        fetchData();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const openDialog = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({ 
        nome: item.nome, 
        tipologia_id: item.tipologia_id || "",
        telefono: item.telefono || "", 
        email: item.email || "",
        note: item.note || "",
        locali_ids: item.locali_ids || []
      });
    } else {
      setEditItem(null);
      // Se c'è un solo locale, selezionalo automaticamente
      const defaultLocaliIds = locali && locali.length === 1 ? [locali[0].id] : [];
      setForm({ nome: "", tipologia_id: "", telefono: "", email: "", note: "", locali_ids: defaultLocaliIds });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error("Nome / Ragione Sociale obbligatorio"); return; }
    if (!form.tipologia_id) { toast.error("Categoria obbligatoria"); return; }
    if (!form.telefono.trim()) { toast.error("Telefono obbligatorio"); return; }
    if (locali && locali.length > 1 && form.locali_ids.length === 0) {
      toast.error("Seleziona almeno un locale servito");
      return;
    }

    try {
      const payload = {
        ...form,
        tipologia_id: form.tipologia_id || null
      };
      
      if (editItem) {
        await axios.put(`${API}/tecnici/${editItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Tecnico aggiornato");
      } else {
        await axios.post(`${API}/tecnici`, payload, { headers: getAuthHeaders() });
        toast.success("Tecnico aggiunto");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/tecnici/${deleteId}`, { headers: getAuthHeaders() });
      toast.success("Tecnico eliminato");
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Tipologie handlers
  const openTipologiaDialog = (tip = null) => {
    if (tip) {
      setEditTipologia(tip);
      setTipologiaForm({ 
        nome: tip.nome, 
        icona: tip.icona,
        keywords: (tip.keywords || []).join(", ")
      });
    } else {
      setEditTipologia(null);
      setTipologiaForm({ nome: "", icona: "🛠️", keywords: "" });
    }
    setTipologiaDialogOpen(true);
  };

  const handleTipologiaSubmit = async (e) => {
    e.preventDefault();
    if (!tipologiaForm.nome) {
      toast.error("Inserisci il nome");
      return;
    }
    try {
      const payload = {
        nome: tipologiaForm.nome,
        icona: tipologiaForm.icona,
        keywords: tipologiaForm.keywords.split(",").map(k => k.trim()).filter(Boolean)
      };
      
      if (editTipologia) {
        await axios.put(`${API}/tipologie-tecnico/${editTipologia.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Tipologia aggiornata");
      } else {
        await axios.post(`${API}/tipologie-tecnico`, payload, { headers: getAuthHeaders() });
        toast.success("Tipologia creata");
      }
      setTipologiaDialogOpen(false);
      setEditTipologia(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteTipologia = async () => {
    if (!deleteTipologiaId) return;
    try {
      await axios.delete(`${API}/tipologie-tecnico/${deleteTipologiaId}`, { headers: getAuthHeaders() });
      toast.success("Tipologia eliminata");
      setDeleteTipologiaId(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Helper per tipologia
  const getTipologia = (id) => tipologie.find(t => t.id === id);

  const filteredItems = items.filter(item => {
    const matchTipologia = filtroTipologia === "all" || item.tipologia_id === filtroTipologia;
    return matchTipologia;
  });

  // Raggruppa per tipologia
  const groupedItems = filtroTipologia === "all" 
    ? tipologie.map(tip => ({
        tipologia: tip,
        tecnici: filteredItems.filter(t => t.tipologia_id === tip.id)
      })).concat([{
        tipologia: { id: null, nome: "Altro", icona: "📋" },
        tecnici: filteredItems.filter(t => !t.tipologia_id)
      }]).filter(g => g.tecnici.length > 0)
    : [{ tipologia: getTipologia(filtroTipologia) || { nome: "Tecnici" }, tecnici: filteredItems }];

  return (
    <div className="space-y-6" data-testid="tecnici-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
            <Wrench className="h-6 w-6 text-cyan-400" />
            Contatti utili
          </h1>
          <p className="text-muted-foreground mt-1">Assistenza e manutenzione</p>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openTipologiaDialog()}
            className="border-border text-foreground hover:bg-muted"
          >
            <Settings className="h-4 w-4 mr-2" />
            Tipologie
          </Button>
        )}
      </div>

      {/* Search hint */}
      <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-cyan-400 text-sm font-medium">Ricerca intelligente</p>
            <p className="text-muted-foreground text-sm">
              Cerca per problema: es. "frigo rotto", "condizionatore", "internet"
            </p>
          </div>
        </div>
      </div>

      {/* Search + Filters + Add */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
          <Input
            placeholder="Cerca tecnico o descrivi il problema..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-14 pl-12 bg-card border-border text-white placeholder:text-white/30 rounded-2xl"
          />
        </div>
        
        {/* Filtro tipologia */}
        <Select value={filtroTipologia} onValueChange={setFiltroTipologia}>
          <SelectTrigger className="w-full sm:w-48 h-14 bg-card border-border text-foreground rounded-2xl">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-foreground">Tutte le categorie</SelectItem>
            {tipologie.map(tip => (
              <SelectItem key={tip.id} value={tip.id} className="text-foreground">
                {tip.icona} {tip.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {isAdmin && (
          <button 
            onClick={() => openDialog()}
            className="h-14 px-6 bg-cyan-400 text-black rounded-2xl font-bold flex items-center gap-2 active:scale-[0.98] transition-all"
          >
            <Plus className="h-5 w-5" />
            <span className="hidden sm:inline">Nuovo</span>
          </button>
        )}
      </div>

      {/* Lista Tecnici */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-card rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-card rounded-3xl backdrop-blur-sm border border-border">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
            <Wrench className="h-10 w-10 text-white/30" />
          </div>
          <p className="text-muted-foreground text-center">
            {searchTerm ? "Nessun tecnico trovato per questa ricerca" : "Nessun tecnico registrato"}
          </p>
          {searchTerm && (
            <Button 
              variant="link" 
              onClick={() => setSearchTerm("")}
              className="mt-2 text-cyan-400"
            >
              Mostra tutti i tecnici
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedItems.map((group) => (
            <div key={group.tipologia?.id || "no-cat"}>
              {/* Header Tipologia */}
              {filtroTipologia === "all" && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{group.tipologia?.icona}</span>
                  <h3 className="text-foreground/70 font-medium">{group.tipologia?.nome}</h3>
                  <span className="text-white/30 text-sm">({group.tecnici.length})</span>
                </div>
              )}
              
              {/* Tecnici */}
              <div className="grid gap-3 sm:grid-cols-2">
                {group.tecnici.map((item) => {
                  const tip = getTipologia(item.tipologia_id);
                  return (
                    <div
                      key={item.id}
                      className="bg-card backdrop-blur-sm rounded-2xl p-4 border border-border hover:bg-muted transition-all"
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className="w-12 h-12 bg-cyan-400/20 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                          {tip?.icona || "🛠️"}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white truncate">{item.nome}</h3>
                          {tip && (
                            <span className="text-xs px-2 py-0.5 bg-cyan-500/20 rounded-full text-cyan-400">
                              {tip.nome}
                            </span>
                          )}
                          
                          <div className="flex flex-col gap-1 mt-2">
                            {item.telefono && (
                              <a 
                                href={`tel:${item.telefono}`}
                                className="text-cyan-400 text-sm flex items-center gap-2 hover:underline"
                              >
                                <Phone className="h-4 w-4" /> {item.telefono}
                              </a>
                            )}
                            {item.email && (
                              <a 
                                href={`mailto:${item.email}`}
                                className="text-muted-foreground text-sm flex items-center gap-2 hover:text-white truncate"
                              >
                                <Mail className="h-4 w-4 flex-shrink-0" /> {item.email}
                              </a>
                            )}
                          </div>
                          
                          {item.note && (
                            <p className="text-muted-foreground text-xs mt-2 line-clamp-2">{item.note}</p>
                          )}
                          
                          {/* Locali serviti */}
                          {item.locali_ids && item.locali_ids.length > 0 && locali && (
                            <div className="flex items-center gap-1 mt-2">
                              <MapPin className="h-3 w-3 text-blue-400" />
                              <span className="text-blue-400 text-xs">
                                {item.locali_ids.map(lid => locali.find(l => l.id === lid)?.nome).filter(Boolean).join(", ")}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actions - Solo Admin */}
                        {isAdmin && (
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => openDialog(item)}
                              className="p-2 rounded-xl hover:bg-muted active:bg-white/20 transition-colors"
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => setDeleteId(item.id)}
                              className="p-2 rounded-xl hover:bg-red-500/20 active:bg-red-500/30 transition-colors"
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog Tecnico */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wrench className="h-5 w-5 text-cyan-400" />
              {editItem ? "Modifica Tecnico" : "Nuovo Tecnico"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Nome / Ragione Sociale *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="es. Tecnico Rossi"
                className="h-12 bg-card border-border text-white rounded-2xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground/70">Categoria</Label>
              <Select 
                value={form.tipologia_id || "none"} 
                onValueChange={(v) => setForm(prev => ({ ...prev, tipologia_id: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-12 bg-card border-border text-foreground rounded-2xl">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none" className="text-muted-foreground">Nessuna categoria</SelectItem>
                  {tipologie.map(tip => (
                    <SelectItem key={tip.id} value={tip.id} className="text-foreground">
                      {tip.icona} {tip.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground/70">Telefono</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm(prev => ({ ...prev, telefono: e.target.value }))}
                placeholder="es. 333 1234567"
                className="h-12 bg-card border-border text-white rounded-2xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground/70">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="es. tecnico@email.it"
                className="h-12 bg-card border-border text-white rounded-2xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground/70">Note</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Orari, specializzazioni, note..."
                className="bg-card border-border text-white rounded-2xl min-h-[60px]"
              />
            </div>

            {/* Selezione Locali */}
            {locali && locali.length > 0 && (
              <div className="space-y-2">
                <Label className="text-foreground/70 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Locali serviti
                  {locali.length > 1 && <span className="text-red-500">*</span>}
                </Label>
                <div className="grid grid-cols-1 gap-2 p-3 bg-muted rounded-2xl">
                  {locali.length === 1 ? (
                    <label className="flex items-center gap-3 p-2 rounded-xl bg-card/50 cursor-not-allowed">
                      <Checkbox checked={true} disabled className="opacity-60" />
                      <span className="text-white">{locali[0].nome}</span>
                      <span className="text-xs text-muted-foreground ml-auto">Default</span>
                    </label>
                  ) : (
                    locali.map(locale => (
                      <label key={locale.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-card cursor-pointer">
                        <Checkbox
                          checked={form.locali_ids.includes(locale.id)}
                          onCheckedChange={() => toggleLocale(locale.id)}
                        />
                        <span className="text-white">{locale.nome}</span>
                      </label>
                    ))
                  )}
                </div>
                {locali.length > 1 && form.locali_ids.length === 0 && (
                  <p className="text-xs text-red-500">Seleziona almeno un locale</p>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-400 hover:bg-cyan-300 text-black font-bold">
                {editItem ? "Salva" : "Aggiungi"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Tipologie */}
      <Dialog open={tipologiaDialogOpen} onOpenChange={(open) => { setTipologiaDialogOpen(open); if (!open) setEditTipologia(null); }}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="h-5 w-5 text-cyan-400" />
              {editTipologia ? "Modifica Categoria" : "Gestione Categorie"}
            </DialogTitle>
          </DialogHeader>
          
          {!editTipologia ? (
            <div className="space-y-4">
              {/* Lista tipologie esistenti */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tipologie.map(tip => (
                  <div key={tip.id} className="p-3 bg-card rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{tip.icona}</span>
                        <span className="text-foreground">{tip.nome}</span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openTipologiaDialog(tip)}
                          className="p-1.5 rounded-lg hover:bg-muted"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => setDeleteTipologiaId(tip.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/20"
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                    {tip.keywords?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1 ml-8">
                        Parole chiave: {tip.keywords.join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Form nuova tipologia */}
              <div className="border-t border-border pt-4">
                <p className="text-muted-foreground text-sm mb-3">Aggiungi nuova categoria</p>
                <form onSubmit={handleTipologiaSubmit} className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={tipologiaForm.icona}
                      onChange={(e) => setTipologiaForm(prev => ({ ...prev, icona: e.target.value }))}
                      placeholder="🛠️"
                      className="w-16 h-12 bg-card border-border text-white text-center rounded-xl"
                      maxLength={2}
                    />
                    <Input
                      value={tipologiaForm.nome}
                      onChange={(e) => setTipologiaForm(prev => ({ ...prev, nome: e.target.value }))}
                      placeholder="Nome categoria"
                      className="flex-1 h-12 bg-card border-border text-white rounded-xl"
                    />
                  </div>
                  <Input
                    value={tipologiaForm.keywords}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, keywords: e.target.value }))}
                    placeholder="Parole chiave (separate da virgola)"
                    className="h-12 bg-card border-border text-white rounded-xl"
                  />
                  <Button type="submit" className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-bold">
                    <Plus className="h-5 w-5 mr-2" />
                    Aggiungi Categoria
                  </Button>
                </form>
              </div>
            </div>
          ) : (
            <form onSubmit={handleTipologiaSubmit} className="space-y-4">
              <div className="flex gap-3">
                <div className="space-y-2">
                  <Label className="text-foreground/70">Icona</Label>
                  <Input
                    value={tipologiaForm.icona}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, icona: e.target.value }))}
                    className="w-20 h-12 bg-card border-border text-white text-center rounded-xl text-xl"
                    maxLength={2}
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-foreground/70">Nome *</Label>
                  <Input
                    value={tipologiaForm.nome}
                    onChange={(e) => setTipologiaForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Nome categoria"
                    className="h-12 bg-card border-border text-white rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/70">Parole chiave per ricerca</Label>
                <Input
                  value={tipologiaForm.keywords}
                  onChange={(e) => setTipologiaForm(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="frigo, frigorifero, congelatore..."
                  className="h-12 bg-card border-border text-white rounded-xl"
                />
                <p className="text-xs text-muted-foreground">Separate da virgola. Servono per la ricerca intelligente.</p>
              </div>
              <DialogFooter className="gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditTipologia(null)} className="flex-1 border-border text-foreground hover:bg-muted">
                  Indietro
                </Button>
                <Button type="submit" className="flex-1 bg-cyan-400 hover:bg-cyan-300 text-black font-bold">
                  Salva
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Tecnico */}
      <DeleteConfirmDialog 
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Elimina Tecnico"
        message="Vuoi eliminare questo tecnico? L'operazione non può essere annullata."
      />
      
      {/* Delete Confirm Tipologia */}
      <DeleteConfirmDialog 
        open={!!deleteTipologiaId}
        onClose={() => setDeleteTipologiaId(null)}
        onConfirm={handleDeleteTipologia}
        title="Elimina Categoria"
        message="Vuoi eliminare questa categoria? I tecnici associati rimarranno senza categoria."
      />
    </div>
  );
};

export default Tecnici;
